<template>
  <div class="search-results-page position-relative vh-adjust mb-6">
    <!-- 헤더 영역 -->
    <div class="search-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="search-title mb-1">{{ formattedTitle }}</h2>
          <p v-if="filteredResults && filteredResults.length > 0" class="result-count mb-0">
            {{
              lang === 'ENG'
                ? `${filteredResults.length} results found`
                : `총 ${filteredResults.length}개의 검색 결과`
            }}
          </p>
        </div>
        <b-button variant="outline-primary" class="exit-btn" @click="exitSearch">
          <b-icon icon="x-lg" class="mr-1" />
          {{ placeholderText.exit }}
        </b-button>
      </div>
    </div>

    <!-- 필터 및 검색 영역 -->
    <div class="controls-area mb-4">
      <b-row>
        <b-col cols="12" md="6" class="mb-3 mb-md-0">
          <div class="per-page-wrapper">
            <label for="per-page-select" class="control-label">{{
              lang === 'ENG' ? 'Show' : '페이지당'
            }}</label>
            <b-form-select
              id="per-page-select"
              v-model="perPage"
              :options="[5, 10, 15, 20]"
              class="per-page-select"
            />
            <span class="control-label ml-2">{{ lang === 'ENG' ? 'per page' : '개씩 보기' }}</span>
          </div>
        </b-col>
        <b-col cols="12" md="6">
          <div class="search-input-wrapper">
            <b-icon icon="search" class="search-icon" />
            <b-form-input
              id="search-input"
              v-model="searchTerm"
              :placeholder="placeholderText.placeholder"
              class="search-input"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="!filteredResults || loading" class="loading-area">
      <b-spinner label="Spinning" variant="primary" />
      <p class="mt-3">{{ lang === 'ENG' ? 'Searching...' : '검색 중...' }}</p>
    </div>

    <!-- 결과 없음 -->
    <div v-else-if="!!filteredResults && filteredResults.length === 0" class="no-results">
      <b-icon icon="search" font-scale="3" class="mb-3 text-muted" />
      <p class="text-muted">{{ placeholderText.noResults }}</p>
    </div>

    <!-- 검색 결과 리스트 -->
    <div v-else class="results-list">
      <div
        v-for="(result, index) in paginatedResults"
        :key="index"
        class="result-card"
        @click="movePage(result)"
      >
        <div class="result-header">
          <span class="result-number">{{ (currentPage - 1) * perPage + index + 1 }}</span>
          <div class="breadcrumb-path">
            <b-icon icon="house-door-fill" class="home-icon" />
            <span class="separator">/</span>
            <span class="path-item">{{
              lang === 'ENG' && result.lv1_info.name_e
                ? result.lv1_info.name_e
                : result.lv1_info.name
            }}</span>
            <span class="separator">/</span>
            <span class="path-item">{{
              lang === 'ENG' && result.lv2_info.name_e
                ? result.lv2_info.name_e
                : result.lv2_info.name
            }}</span>
          </div>
        </div>
        <div class="result-content">
          <p
            class="result-text"
            v-html="highlightKeyword(lang === 'ENG' && result.text_e ? result.text_e : result.text)"
          ></p>
        </div>
        <div class="result-footer">
          <span class="view-more">
            {{ lang === 'ENG' ? 'View details' : '자세히 보기' }}
            <b-icon icon="arrow-right" class="ml-1" />
          </span>
        </div>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <b-pagination
      v-if="filteredResults && filteredResults.length > 0"
      v-model="currentPage"
      :per-page="perPage"
      :total-rows="filteredResults.length"
      align="center"
      aria-controls="table-pagination"
      class="custom-pagination mt-4"
      first-number
      last-number
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { escapeHtml } from '@/components/sanitize';

export default {
  name: 'SearchResults',
  props: {
    // 💡 5. lang prop을 추가하여 부모로부터 언어 설정을 받음
    lang: {
      type: String,
      default: 'KOR'
    },
    searchResults: {
      type: Array,
      default: function () {
        return null;
      }
    },
    loading: {
      type: Boolean,
      default: true
    },
    searchKeyword: {
      type: String,
      default: ''
    },
    mainColor: {
      type: String,
      default: '#05adc8'
    },
    subColor: {
      type: String,
      default: '#05adc8'
    }
  },
  data: function () {
    return {
      perPage: 10,
      currentPage: 1,
      searchTerm: ''
    };
  },
  computed: {
    ...mapState('report_list', ['view_mode']),

    // 색상 투명도 버전
    mainColorLight() {
      return this.mainColor + '1A'; // 10% opacity
    },

    // 💡 6. UI 텍스트를 언어에 따라 동적으로 제공하는 computed 속성
    placeholderText() {
      if (this.lang === 'ENG') {
        return {
          exit: 'Exit',
          placeholder: 'Enter search term...',
          noResults: 'No search results found.',
          resultsFor: `Results for '${this.searchKeyword}'`,
          results: 'Search Results'
        };
      }
      return {
        exit: '나가기',
        placeholder: '검색어를 입력하세요...',
        noResults: '검색 결과가 없습니다.',
        resultsFor: `'${this.searchKeyword}' 검색 결과`,
        results: '검색 결과'
      };
    },

    formattedTitle: function () {
      return this.searchKeyword ? this.placeholderText.resultsFor : this.placeholderText.results;
    },

    // 💡 7. 핵심 변경: lang 값에 따라 다른 데이터 필드를 검색하도록 수정
    filteredResults: function () {
      if (this.searchResults === null) {
        return null;
      }
      if (!this.searchTerm) {
        return this.searchResults;
      }
      const lowerCaseSearchTerm = this.searchTerm.toLowerCase();

      // lang prop 값을 기준으로 필터링 로직을 분기합니다.
      if (this.lang === 'ENG') {
        return this.searchResults.filter(
          item =>
            (item.lv1_info.name_e &&
              item.lv1_info.name_e.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (item.lv2_info.name_e &&
              item.lv2_info.name_e.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (item.text_e && item.text_e.toLowerCase().includes(lowerCaseSearchTerm))
        );
      } else {
        // 'ko' 또는 기본값
        return this.searchResults.filter(
          item =>
            (item.lv1_info.name &&
              item.lv1_info.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (item.lv2_info.name &&
              item.lv2_info.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
            (item.text && item.text.toLowerCase().includes(lowerCaseSearchTerm))
        );
      }
    },

    paginatedResults: function () {
      if (!this.filteredResults) {
        return [];
      }
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredResults.slice(start, end);
    }
  },
  methods: {
    ...mapMutations('story_board', ['update_selected_lv1', 'update_selected_lv2']),
    ...mapMutations('search', ['setSearchKeyword']),

    // [개선] 검색어 하이라이트 기능
    highlightKeyword(text) {
      if (!text) return '';

      const sanitizedText = escapeHtml(text);

      const keyword = this.searchKeyword || this.searchTerm;
      if (!keyword || !keyword.trim()) return sanitizedText;

      // 정규식 특수문자 이스케이프 (예: "1.0", "(ESG)" 등 오동작 방지)
      const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');
      return sanitizedText.replace(regex, '<mark class="highlight">$1</mark>');
    },

    exitSearch() {
      this.setSearchKeyword('');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    movePage: function (selected_disclosure) {
      // [수정] 검색어를 쿼리에 저장하고 SearchResultPage를 닫음
      const currentSearchKeyword = this.searchKeyword;
      this.setSearchKeyword('');
      this.$emit('closeSearch');

      // [수정] $nextTick으로 DOM 업데이트 후 라우팅
      this.$nextTick(() => {
        // [개선] 라우터를 통한 navigation으로 일관성 유지
        // 라우터를 사용하면 StoryBoardViewer의 $route watcher가 자동으로
        // TOC에서 완전한 lv1_info, lv2_info를 찾아서 업데이트함
        const query = {
          lv1: selected_disclosure.lv1_info.key,
          lv2: selected_disclosure.lv2_info.key,
          q_str: currentSearchKeyword // [추가] 검색어를 쿼리 파라미터로 전달
        };

        if (this.lang === 'ENG') {
          query.l = 'ENG';
        }

        this.$router
          .push({
            query: query,
            hash: '#order_id_' + selected_disclosure.order_index
          })
          .catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error(err);
            }
          });
      });
    }
  },
  watch: {
    perPage: function () {
      this.currentPage = 1;
    },
    searchTerm: function () {
      this.currentPage = 1;
    },
    searchResults: function () {
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped>
.search-results-page {
  padding: 0;
}

/* 헤더 영역 */
.search-header {
  border-bottom: 2px solid v-bind(mainColor);
  padding-bottom: 1rem;
}

.search-title {
  font-size: 28px;
  font-weight: 700;
  color: #121212;
  margin: 0;
}

.result-count {
  font-size: 14px;
  color: #666666;
}

.exit-btn {
  border-color: v-bind(mainColor);
  color: v-bind(mainColor);
  font-weight: 600;
  transition: all 0.3s ease;
}

.exit-btn:hover {
  background-color: v-bind(mainColor);
  border-color: v-bind(mainColor);
  color: #fff;
}

/* 컨트롤 영역 */
.controls-area {
  background-color: #f7f7f9;
  padding: 1rem;
  border-radius: 8px;
}

.per-page-wrapper {
  display: flex;
  align-items: center;
}

.control-label {
  font-size: 14px;
  color: #666666;
  margin: 0;
  white-space: nowrap;
}

.per-page-select {
  width: 80px;
  margin: 0 0.5rem;
  border-color: #ebebeb;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  z-index: 2;
}

.search-input {
  padding-left: 36px;
  border-color: #ebebeb;
}

.search-input:focus {
  border-color: v-bind(mainColor);
}

/* 로딩 영역 */
.loading-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  color: #666666;
}

/* 결과 없음 */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
}

/* 검색 결과 카드 */
.results-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.result-card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.result-card:hover {
  border-color: v-bind(mainColor);
  transform: translateY(-2px);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.result-number {
  color: v-bind(mainColor);
  font-weight: 600;
}

.breadcrumb-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 14px;
  color: #666666;
  line-height: 1.6;
}

.home-icon {
  color: v-bind(mainColor);
}

.separator {
  color: #cccccc;
}

.path-item {
  color: #121212;
  font-weight: 500;
}

.result-content {
  margin-bottom: 0.75rem;
}

.result-text {
  font-size: 15px;
  line-height: 1.6;
  color: #333333;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 검색어 하이라이트 */
.result-text :deep(mark.highlight) {
  background-color: #fff59d;
  color: #121212;
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: 600;
}

.result-footer {
  display: flex;
  justify-content: flex-end;
}

.view-more {
  color: v-bind(mainColor);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* 페이지네이션 */
.custom-pagination :deep(.page-link) {
  color: v-bind(mainColor);
  border-color: #ebebeb;
}

.custom-pagination :deep(.page-item.active .page-link) {
  background-color: v-bind(mainColor);
  border-color: v-bind(mainColor);
  color: #fff;
}

.custom-pagination :deep(.page-link:hover) {
  background-color: v-bind(mainColorLight);
  border-color: v-bind(mainColor);
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .search-title {
    font-size: 22px;
  }

  .result-card {
    padding: 1rem;
  }

  .breadcrumb-path {
    font-size: 13px;
  }

  .result-text {
    font-size: 14px;
    -webkit-line-clamp: 2;
  }

  .exit-btn {
    padding: 0.375rem 0.75rem;
    font-size: 14px;
  }
}

@media (max-width: 576px) {
  .search-header {
    padding-bottom: 0.75rem;
  }

  .search-title {
    font-size: 20px;
  }

  .result-count {
    font-size: 13px;
  }

  .controls-area {
    padding: 0.75rem;
  }

  .per-page-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .per-page-select {
    margin: 0;
  }

  .result-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .breadcrumb-path {
    font-size: 12px;
  }
}
</style>
